import { useState, useEffect } from 'react';
import { Document, DocumentStatus } from '../../types/document.ts';
import { ArrowLeft, Clock, User, FileText, GitBranch, CheckCircle, XCircle, AlertCircle, FileEdit, CheckCircle2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useNotify } from 'react-admin';
import { useActionPermissionAlwaysRemote as useActionPermission } from '../../hooks/useActionPermissionAlwaysRemote.ts';
import { commitDocument, getDocumentApprovalInfo, getDocumentById, getDocumentVersionsByDocumentId, replaceApproverWithDeputy, updateDocument } from '../../services/documentService.ts';
import { getAllUsers } from '../../services/userService.ts';
import { getChangeRequestsByDocumentId, type ChangeRequest } from '../../services/changeRequestService.ts';


export default function DocumentManageDetail() {
   const { id } = useParams(); // 来自 /documentsList/:id/show
  const navigate = useNavigate();
  const notify = useNotify();
  const { can } = useActionPermission();
  const canOverwriteDocument = can('documentslist:overwrite', { menu: 'documentsList', allowGlobalFallback: false });

  const [document, setDocument] = useState<Document | undefined>();
  const [loading, setLoading] = useState(!!id);
  const [showCommitModal, setShowCommitModal] = useState(false);
  const [commitAction] = useState<'Approve' | 'Reject'>('Approve');
  const [commitComments, setCommitComments] = useState('');
  const [committing, setCommitting] = useState(false);
  const [showObsoleteModal, setShowObsoleteModal] = useState(false);
  const [markingObsolete, setMarkingObsolete] = useState(false);
  const [userNameMap, setUserNameMap] = useState<Record<string, string>>({});
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [showDeputyModal, setShowDeputyModal] = useState(false);
  const [selectedStepInstanceId, setSelectedStepInstanceId] = useState<string>('');
  const [selectedFromUserId, setSelectedFromUserId] = useState<string>('');
  const [selectedDeputyUserId, setSelectedDeputyUserId] = useState<string>('');
  const [deputyComment, setDeputyComment] = useState('');
  const [savingDeputy, setSavingDeputy] = useState(false);
  const [changeRequests, setChangeRequests] = useState<ChangeRequest[]>([]);

  useEffect(() => {
    if (id) {
      const loadDocument = async () => {
        try {
          const [doc, users, approvalRecords, versions, crs] = await Promise.all([
            getDocumentById(id),
            getAllUsers(),
            getDocumentApprovalInfo(id),
            getDocumentVersionsByDocumentId(id),
            getChangeRequestsByDocumentId(id),
          ]);
          setChangeRequests(crs);

          const map: Record<string, string> = {};
          users.forEach((user: any) => {
            const userId = user?.id ?? user?.Id ?? user?.userId;
            const userName = user?.userName ?? user?.username ?? user?.name;
            if (userId != null && userName) {
              map[String(userId)] = String(userName);
            }
          });
          setUserNameMap(map);
          setAllUsers(users || []);

          const enrichedDoc: Document = {
            ...doc,
            versions: versions.length > 0 ? versions : (doc.versions || []),
            approvalHistory: approvalRecords.length > 0 ? approvalRecords : (doc.approvalHistory || []),
          };

          setDocument(enrichedDoc);
          setLoading(false);
        } catch (error: any) {
          console.error('Failed to load document:', error);
          notify(`Failed to load document: ${error.message}`, { type: 'error' });
          setLoading(false);
        }
      };
      loadDocument();
    }
  }, [id, notify]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div className="text-gray-600">Loading document...</div>
        </div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="p-6">
        <div className="text-center text-gray-500">Document not found</div>
      </div>
    );
  }

  const getStatusColor = (status: DocumentStatus) => {
    switch (status) {
      case 'Draft':
        return 'bg-gray-100 text-gray-700';
      case 'Review':
        return 'bg-yellow-100 text-yellow-700';
      case 'Approved':
        return 'bg-green-100 text-green-700';
      case 'Obsolete':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusIcon = (status: DocumentStatus) => {
    switch (status) {
      case 'Approved':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'Review':
        return <AlertCircle className="w-5 h-5 text-yellow-600" />;
      case 'Obsolete':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <FileText className="w-5 h-5 text-gray-600" />;
    }
  };

  const getUserDisplayName = (rawValue?: string) => {
    if (!rawValue) return '-';
    const normalized = String(rawValue);
    return userNameMap[normalized] || normalized;
  };

  const getOwnerDisplayName = () => {
    const ownerName = (document as any)?.ownerName;
    if (ownerName) return String(ownerName);
    const ownerId = (document as any)?.ownerId ?? (document as any)?.owner;
    if (!ownerId) return '-';
    return getUserDisplayName(String(ownerId));
  };

  const renderApprovalComments = (comments?: string) => {
    const raw = String(comments || '').trim();
    if (!raw) return '';

    const tokenRegex = /(username|userId)\s*[=:]\s*([^\s,;，；]+)/gi;
    const nodes: Array<string | any> = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = tokenRegex.exec(raw)) !== null) {
      const [fullMatch, key, value] = match;
      const start = match.index;

      if (start > lastIndex) {
        nodes.push(raw.slice(lastIndex, start));
      }

      const normalizedKey = String(key || '').toLowerCase();
      const rawValue = String(value || '').trim();
      const displayName = normalizedKey === 'userid' ? getUserDisplayName(rawValue) : rawValue;

      nodes.push(
        <span
          key={`delegated-user-${start}`}
          className="inline-block px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold"
        >
          {displayName}
        </span>
      );

      lastIndex = start + fullMatch.length;
    }

    if (lastIndex < raw.length) {
      nodes.push(raw.slice(lastIndex));
    }

    return nodes.length > 0 ? nodes : raw;
  };

  

  const handleCommit = async () => {
    if (!id) return;
    setCommitting(true);
    try {
      await commitDocument(id, commitAction, commitComments);
      notify(`Document ${commitAction === 'Approve' ? 'approved' : 'rejected'} successfully!`, { type: 'success' });
      setShowCommitModal(false);
      setCommitComments('');
      // Optionally refresh or redirect
      setTimeout(() => {
        navigate(-1);
      }, 1500);
    } catch (error: any) {
      notify(error.message || 'Failed to commit document', { type: 'error' });
    } finally {
      setCommitting(false);
    }
  };

  const handleMarkObsolete = async () => {
    if (!id || !document) return;
    
    setMarkingObsolete(true);
    try {
      // Update document status to Obsolete
      const updateData = {
        title: document.title,
        description: document.description,
        category: document.category,
        ownerId: (document as any)?.ownerId ?? (document as any)?.owner,
        status: 'Obsolete',
        tags: document.tags,
      };
      
      await updateDocument(id, updateData as any);
      notify('Document marked as Obsolete successfully!', { type: 'success' });
      setShowObsoleteModal(false);
      
      // Update local state
      setDocument({ ...document, status: 'Obsolete' });
      
      // Redirect after a short delay
      setTimeout(() => {
        navigate(-1);
      }, 1500);
    } catch (error: any) {
      console.error('Failed to mark document as obsolete:', error);
      notify(`Failed to mark as obsolete: ${error.message || 'Unknown error'}`, { type: 'error' });
    } finally {
      setMarkingObsolete(false);
    }
  };

  const openDeputyModal = (stepInstanceId: string, fromUserId: string) => {
    setSelectedStepInstanceId(String(stepInstanceId));
    setSelectedFromUserId(String(fromUserId));
    setSelectedDeputyUserId('');
    setDeputyComment('');
    setShowDeputyModal(true);
  };

  const handleAssignDeputy = async () => {
    if (!id) return;
    if (!selectedStepInstanceId) {
      notify('Step instance not found', { type: 'warning' });
      return;
    }
    if (!selectedFromUserId) {
      notify('Original approver not found', { type: 'warning' });
      return;
    }
    if (!selectedDeputyUserId) {
      notify('Please select deputy approver', { type: 'warning' });
      return;
    }

    setSavingDeputy(true);
    try {
      await replaceApproverWithDeputy(
        selectedStepInstanceId,
        id,
        selectedFromUserId,
        selectedDeputyUserId,
        deputyComment
      );
      notify('Deputy approver assigned successfully', { type: 'success' });

      const refreshedApproval = await getDocumentApprovalInfo(id);
      setDocument((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          approvalHistory: refreshedApproval.length > 0 ? refreshedApproval : prev.approvalHistory,
        };
      });

      setShowDeputyModal(false);
      setSelectedStepInstanceId('');
      setSelectedFromUserId('');
      setSelectedDeputyUserId('');
      setDeputyComment('');
    } catch (error: any) {
      notify(error?.message || 'Failed to assign deputy approver', { type: 'error' });
    } finally {
      setSavingDeputy(false);
    }
  };

  function onBack(): void {
    navigate(-1);
  }

  function onEdit(id: string): void {
    navigate(`/documentsList/${id}/edit`);
  }

  return (
    <div className="w-full h-full overflow-auto flex flex-col bg-transparent">
      <div className="flex-1 pb-4 overflow-auto">
      {/* Header */}
      <div className="mb-5 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900 mb-3"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to List
        </button>

        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              {getStatusIcon(document.status)}
              <h2 className="text-[22px] font-extrabold text-slate-900 leading-none">{document.title}</h2>
            </div>
            <div className="text-gray-600 mb-2">{document.description}</div>
            <div className="flex items-center gap-4 text-gray-500">
              <span>Document Number: {document.documentNumber}</span>
              <span>•</span>
              <span>Current Version: {document.currentVersion}</span>
            </div>
          </div>

            <div className="flex gap-2">
            {document.status === 'Review' && (
              <>
                {/* <button
                  onClick={() => {
                    setCommitAction('Approve');
                    setShowCommitModal(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  title="Approve this document"
                >
                  <ThumbsUp className="w-4 h-4" />
                  Approve
                </button>
                <button
                  onClick={() => {
                    setCommitAction('Reject');
                    setShowCommitModal(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  title="Reject this document"
                >
                  <ThumbsDown className="w-4 h-4" />
                  Reject
                </button> */}
              </>
            )}
            {canOverwriteDocument && document.status === 'Approved' && (
              <button
                onClick={() => navigate(`/documentsList/${document.id}/edit?mode=overwrite`)}
                className="inline-flex items-center px-3.5 h-9 border border-amber-300 text-amber-700 rounded-[10px] hover:bg-amber-50 transition-colors font-semibold text-sm"
              >
                Overwrite
              </button>
            )}
            {document.status !== 'Obsolete' && document.status !== 'Approved' && (
              <button
                onClick={() => onEdit(document.id)}
                className="inline-flex items-center px-3.5 h-9 border border-slate-300 text-slate-700 rounded-[10px] hover:bg-slate-50 transition-colors font-semibold text-sm"
              >
                Edit
              </button>
            )}
                <button
                  onClick={() => {
                    try {
                      if (!document.draftFileUrl) {
                        notify('Document URL not available', { type: 'warning' });
                        return;
                      }
                      window.open(document.draftFileUrl, '_blank', 'noopener,noreferrer');
                    } catch (err: any) {
                      console.error('Failed to preview document:', err);
                      notify(`Preview failed: ${err?.message || 'Unknown error'}`, { type: 'error' });
                    }
                  }}
                  className="inline-flex items-center px-3.5 h-9 bg-slate-900 text-white rounded-[10px] hover:bg-slate-800 transition-colors font-bold text-sm"
                >
                  Preview
                </button>
          </div>
        
        </div>

        <div className="flex items-center gap-3 text-gray-500 flex-wrap mt-3">
          <span className="text-gray-600">Document Status:</span>
          <span className={`px-2 py-0.5 rounded text-sm font-medium ${getStatusColor(document.status)}`}>
            {document.status === 'Draft' && 'Draft'}
            {document.status === 'Review' && 'Under Review'}
            {document.status === 'Approved' && 'Approved'}
            {document.status === 'Obsolete' && 'Obsolete'}
          </span>
          <span className="text-gray-300">|</span>
          <span className="text-sm">Current Version: <span className="font-medium text-gray-700">{document.currentVersion}</span></span>
          <span className="text-gray-300">|</span>
          <span className="text-sm">Approval Records: <span className="font-medium text-gray-700">{(document.approvalHistory || []).length}</span></span>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-3">
          {/* Basic Info (full width) */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
            <h3 className="text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-2 gap-x-6 gap-y-6">
              <div>
                <div className="text-gray-500 mb-1">Category</div>
                <div className="text-gray-900">
                  <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded">
                    {document.category}
                  </span>
                </div>
              </div>
              <div>
                <div className="text-gray-500 mb-1">Owner</div>
                <div className="text-gray-900 flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-400" />
                  {getOwnerDisplayName()}
                </div>
              </div>
              <div>
                <div className="text-gray-500 mb-1">Description</div>
                <div className="text-gray-900 whitespace-pre-wrap break-words">{document.description || '-'}</div>
              </div>
              <div>
                <div className="text-gray-500 mb-1">Created By</div>
                <div className="text-gray-900 flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-400" />
                  {getUserDisplayName(document.createdBy)}
                </div>
              </div>
              <div>
                <div className="text-gray-500 mb-1">Created Date</div>
                <div className="text-gray-900 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  {new Date(document.createdAt).toLocaleString('en-GB')}
                </div>
              </div>
              <div>
                <div className="text-gray-500 mb-1">Updated By</div>
                <div className="text-gray-900 flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-400" />
                  {getUserDisplayName(document.updatedBy)}
                </div>
              </div>
              <div>
                <div className="text-gray-500 mb-1">Last Updated</div>
                <div className="text-gray-900 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  {new Date(document.updatedAt).toLocaleString('en-GB')}
                </div>
              </div>
              
            </div>
          </div>

        </div>

        {/* Main Content */}
        <div className="col-span-3 space-y-6">

          {/* Version History */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <GitBranch className="w-5 h-5 text-gray-700" />
              <h3 className="text-gray-900 text-lg font-semibold">Version History</h3>
            </div>
            {(document.versions || []).length > 0 ? (
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="py-2 pr-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Version</th>
                    <th className="py-2 pr-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                    <th className="py-2 pr-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Change Notes</th>
                    <th className="py-2 pr-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden sm:table-cell whitespace-nowrap">Date</th>
                    <th className="py-2 pr-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden sm:table-cell">Created By</th>
                    <th className="py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(document.versions || []).map((version, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="py-3 pr-4 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <span className="font-semibold text-gray-900">{version.version}</span>
                          {version.isActive && (
                            <span className="px-1.5 py-0.5 bg-blue-600 text-white rounded-full text-xs font-medium">Current</span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 pr-4 whitespace-nowrap">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(version.status)}`}>
                          {version.status === 'Draft' && 'Draft'}
                          {version.status === 'Review' && 'Under Review'}
                          {version.status === 'Approved' && 'Approved'}
                          {version.status === 'Obsolete' && 'Obsolete'}
                        </span>
                      </td>
                      <td className="py-3 pr-4 max-w-[220px]">
                        <span className="text-gray-700 truncate block">{version.versionChanges || version.changes || '—'}</span>
                      </td>
                      <td className="py-3 pr-4 text-xs text-gray-500 whitespace-nowrap hidden sm:table-cell">
                        {new Date(version.createdAt).toLocaleDateString('en-GB')}
                      </td>
                      <td className="py-3 pr-4 text-xs text-gray-500 whitespace-nowrap hidden sm:table-cell">
                        {getUserDisplayName(version.createdBy)}
                      </td>
                      <td className="py-3 text-right">
                        <button
                          onClick={() => navigate(`/documentsList/${document.id}/versions/${version.id ?? version.version}`)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-blue-700 border border-blue-200 hover:bg-blue-50 rounded-md transition-colors"
                          aria-label={`View version ${version.version}`}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-4 rounded-lg border border-dashed border-gray-300 bg-gray-50 text-sm text-gray-600">
                No version history available.
              </div>
            )}
          </div>

          {/* Change Requests */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <FileEdit className="w-5 h-5 text-gray-700" />
              <h3 className="text-gray-900 text-lg font-semibold">Change Requests</h3>
              {changeRequests.length > 0 && (
                <span className="ml-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">
                  {changeRequests.length}
                </span>
              )}
            </div>
            {changeRequests.length === 0 ? (
              <div className="p-4 rounded-lg border border-dashed border-gray-300 bg-gray-50 text-sm text-gray-600">
                No change requests for this document.
              </div>
            ) : (
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="py-2 pr-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">CR Number</th>
                    <th className="py-2 pr-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Change Reason</th>
                    <th className="py-2 pr-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Requested By</th>
                    <th className="py-2 pr-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Date</th>
                    <th className="py-2 pr-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Approver</th>
                    <th className="py-2 pr-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                    <th className="py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {changeRequests.map((cr) => (
                    <tr key={cr.id} className="hover:bg-gray-50 transition-colors">
                      <td className="py-3 pr-4 whitespace-nowrap">
                        <span className="font-semibold text-gray-700">{cr.crNumber || '-'}</span>
                      </td>
                      <td className="py-3 pr-4 max-w-[220px]">
                        <span className="text-gray-700 line-clamp-2 block">{cr.changeReason || '—'}</span>
                      </td>
                      <td className="py-3 pr-4 text-gray-600 whitespace-nowrap">{cr.requestedByName || '—'}</td>
                      <td className="py-3 pr-4 text-xs text-gray-500 whitespace-nowrap">
                        {new Date(cr.requestedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="py-3 pr-4 text-gray-600 whitespace-nowrap">{cr.approverName || '—'}</td>
                      <td className="py-3 pr-4">
                        {cr.status === 'approved' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                            <CheckCircle2 size={12} /> Approved
                          </span>
                        )}
                        {cr.status === 'rejected' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                            <XCircle size={12} /> Rejected
                          </span>
                        )}
                        {cr.status === 'pending' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">
                            <Clock size={12} /> Pending
                          </span>
                        )}
                      </td>
                      <td className="py-3 text-right">
                        <button
                          onClick={() => navigate(`/changeRequest/${cr.id}/view`)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-blue-700 border border-blue-200 hover:bg-blue-50 rounded-md transition-colors"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Approval History */}
          {(document.approvalHistory || []).length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
              <h3 className="text-gray-900 text-lg font-semibold mb-4">Approval History Of Current Version</h3>
              <div className="space-y-3">
                {(document.approvalHistory || []).map((record) => (
                  <div key={record.id} className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900">{getUserDisplayName(record.approver)}</span>
                        <span className="text-gray-500">•</span>
                        <span className="text-gray-600 text-sm">Level {record.level}</span>
                        <span
                          className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            record.action === 'Approved'
                              ? 'bg-green-100 text-green-700'
                              : record.action === 'Rejected'
                              ? 'bg-red-100 text-red-700'
                              : record.action === 'Deputy'
                              ? 'bg-indigo-100 text-indigo-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}
                        >
                          {record.action === 'Approved' && 'Approved'}
                          {record.action === 'Rejected' && 'Rejected'}
                          {record.action === 'Deputy' && 'DEPUTY'}
                          {record.action === 'Pending' && 'Pending'}
                        </span>
                      </div>
                      <span className="text-gray-500 text-sm flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {new Date(record.timestamp).toLocaleString('en-GB')}
                      </span>
                    </div>
                    {record.action === 'Pending' && String(document.status).trim().toLowerCase() !== 'draft' && (
                      <div className="mb-2">
                        <button
                          onClick={() => openDeputyModal(record.stepInstanceId || '', record.approverId || record.approver)}
                          disabled={!record.stepInstanceId}
                          className="text-xs px-2.5 py-1 border border-indigo-300 text-indigo-700 rounded hover:bg-indigo-50 transition-colors"
                        >
                          Assign Deputy
                        </button>
                      </div>
                    )}
                    {record.comments && (
                      <div className="mt-2 pt-2 border-t border-gray-200">
                        <p className="text-gray-700 text-sm"><span className="font-medium">Comments:</span> {renderApprovalComments(record.comments)}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {(document.approvalHistory || []).length === 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-gray-900 text-lg font-semibold mb-4">Approval History Of Current Version</h3>
              <div className="text-sm text-gray-600">No approval history available.</div>
            </div>
          )}
        </div>

        {/* Sidebar (other sidebar blocks remain) */}
        <div className="space-y-6">
          {/* Related Actions (commented out) */}
        </div>
      </div>

      {/* Mark as Obsolete Modal */}
      {showObsoleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">Mark as Obsolete</h3>
            </div>
            <div className="p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
                <div className="flex-1">
                  <p className="text-gray-900 font-medium mb-1">Mark this document as obsolete?</p>
                  <p className="text-sm text-gray-600">Once marked as obsolete, this approved document will be archived and no longer available for new approvals. This action cannot be easily reversed.</p>
                </div>
              </div>
              {document && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 space-y-1 mb-4">
                  <div className="text-xs text-gray-600"><span className="font-medium">Document:</span> {document.documentNumber}</div>
                  <div className="text-xs text-gray-600"><span className="font-medium">Title:</span> {document.title}</div>
                  <div className="text-xs text-gray-600"><span className="font-medium">Current Status:</span> <span className="inline-block px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">Approved</span></div>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
              <button
                type="button"
                onClick={() => setShowObsoleteModal(false)}
                disabled={markingObsolete}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleMarkObsolete}
                disabled={markingObsolete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {markingObsolete ? 'Marking...' : 'Mark as Obsolete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Commit Modal */}
      {showCommitModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">
                {commitAction === 'Approve' ? 'Approve Document' : 'Reject Document'}
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="text-sm font-medium text-gray-900 mb-2">Document Details</div>
                <div className="text-sm text-gray-600 space-y-1">
                  <div><span className="font-medium">Number:</span> {document.documentNumber}</div>
                  <div><span className="font-medium">Title:</span> {document.title}</div>
                  <div><span className="font-medium">Version:</span> {document.currentVersion}</div>
                  <div><span className="font-medium">Category:</span> {document.category}</div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {commitAction === 'Approve' ? 'Approval Comments (Optional)' : 'Rejection Reason (Optional)'}
                </label>
                <textarea
                  value={commitComments}
                  onChange={(e) => setCommitComments(e.target.value)}
                  placeholder="Enter your comments or reasons..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={4}
                  disabled={committing}
                />
              </div>

              {commitAction === 'Reject' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="text-sm text-red-700">
                    <strong>Warning:</strong> Rejecting this document will send it back for revision.
                  </div>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowCommitModal(false);
                  setCommitComments('');
                }}
                disabled={committing}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCommit}
                disabled={committing}
                className={`flex-1 px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  commitAction === 'Approve'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {committing ? 'Processing...' : (commitAction === 'Approve' ? 'Approve' : 'Reject')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeputyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-xl w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">Assign Deputy Approver</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Deputy User *
                </label>
                <select
                  value={selectedDeputyUserId}
                  onChange={(e) => setSelectedDeputyUserId(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={savingDeputy}
                >
                  <option value="">-- Select user --</option>
                  {allUsers.map((u: any) => {
                    const userId = u?.id ?? u?.Id ?? u?.userId;
                    const userName = u?.userName ?? u?.username ?? u?.name;
                    if (userId == null) return null;
                    return (
                      <option key={String(userId)} value={String(userId)}>
                        {String(userName || userId)}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comment (Optional)
                </label>
                <textarea
                  value={deputyComment}
                  onChange={(e) => setDeputyComment(e.target.value)}
                  placeholder="Reason for deputy assignment"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  disabled={savingDeputy}
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowDeputyModal(false);
                  setSelectedStepInstanceId('');
                  setSelectedFromUserId('');
                  setSelectedDeputyUserId('');
                  setDeputyComment('');
                }}
                disabled={savingDeputy}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAssignDeputy}
                disabled={savingDeputy || !selectedDeputyUserId}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingDeputy ? 'Saving...' : 'Confirm Deputy'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
